<template>
  <div>
    <div class="page-toolbar">
      <div class="page-title">小程序用户管理</div>
      <div style="display:flex; gap: 10px;">
        <el-input
          v-model.trim="keyword"
          placeholder="按昵称/公司/手机号过滤"
          clearable
          style="width: 260px;"
        />
        <el-button icon="el-icon-refresh" @click="loadUsers">刷新</el-button>
      </div>
    </div>

    <el-card class="page-card">
      <el-table :data="filteredUsers" v-loading="loading" stripe>
        <el-table-column type="index" width="56" label="#" />
        <el-table-column label="用户" min-width="230">
          <template slot-scope="{ row }">
            <div class="user-cell">
              <el-avatar :size="34" :src="row.avatar || ''" icon="el-icon-user-solid" />
              <div>
                <div class="u-name">{{ row.nickname || '未设置昵称' }}</div>
                <div class="u-id">{{ row.companyName || '-' }}</div>
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="contactPhone" label="手机号" width="140" />
        <el-table-column label="资质状态" width="120">
          <template slot-scope="{ row }">
            <el-tag size="mini" :type="qualificationTagType(row.qualificationStatus)">
              {{ qualificationStatusLabel(row.qualificationStatus) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="是否供应商" width="110">
          <template slot-scope="{ row }">
            <el-tag size="mini" :type="row.supplier ? 'success' : 'info'">
              {{ row.supplier ? '是' : '否' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="110">
          <template slot-scope="{ row }">
            <el-tag size="mini" :type="row.blacklisted ? 'danger' : 'success'">
              {{ row.blacklisted ? '已拉黑' : '正常' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="updatedAt" label="更新时间" width="170" />
        <el-table-column label="操作" width="230" fixed="right">
          <template slot-scope="{ row }">
            <el-button size="mini" @click="viewUser(row)">查看</el-button>
            <el-button
              size="mini"
              :type="row.blacklisted ? 'success' : 'danger'"
              @click="toggleBlacklist(row)"
            >
              {{ row.blacklisted ? '取消拉黑' : '拉黑' }}
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <el-drawer
      title="用户详情"
      :visible.sync="drawerVisible"
      size="460px"
      direction="rtl"
    >
      <div v-if="currentUser" class="detail-wrap">
        <el-descriptions :column="1" border>
          <el-descriptions-item label="昵称">{{ currentUser.nickname || '-' }}</el-descriptions-item>
          <el-descriptions-item label="头像">
            <el-image
              v-if="currentUser.avatar"
              :src="currentUser.avatar"
              :preview-src-list="[currentUser.avatar]"
              fit="cover"
              class="detail-avatar"
            />
            <span v-else>-</span>
          </el-descriptions-item>
          <el-descriptions-item label="公司">{{ currentUser.companyName || '-' }}</el-descriptions-item>
          <el-descriptions-item label="手机号">{{ currentUser.contactPhone || '-' }}</el-descriptions-item>
          <el-descriptions-item label="资质状态">{{ qualificationStatusLabel(currentUser.qualificationStatus, '-') }}</el-descriptions-item>
          <el-descriptions-item label="供应商">{{ currentUser.supplier ? '是' : '否' }}</el-descriptions-item>
          <el-descriptions-item label="拉黑">{{ currentUser.blacklisted ? '是' : '否' }}</el-descriptions-item>
          <el-descriptions-item label="最近登录">{{ currentUser.lastLoginTime || '-' }}</el-descriptions-item>
          <el-descriptions-item label="创建时间">{{ currentUser.createdAt || '-' }}</el-descriptions-item>
          <el-descriptions-item label="更新时间">{{ currentUser.updatedAt || '-' }}</el-descriptions-item>
        </el-descriptions>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import { fetchUserDetail, fetchUsers, setUserBlacklist } from '../../api/admin'
import { isAuthRedirectError } from '../../utils/auth'

export default {
  name: 'UserListView',
  data() {
    return {
      loading: false,
      users: [],
      keyword: '',
      drawerVisible: false,
      currentUser: null
    }
  },
  computed: {
    filteredUsers() {
      const key = String(this.keyword || '').toLowerCase()
      if (!key) return this.users
      return this.users.filter((u) => {
        const nickname = String(u.nickname || '').toLowerCase()
        const companyName = String(u.companyName || '').toLowerCase()
        const contactPhone = String(u.contactPhone || '').toLowerCase()
        return nickname.includes(key) || companyName.includes(key) || contactPhone.includes(key)
      })
    }
  },
  created() {
    this.loadUsers()
  },
  methods: {
    qualificationTagType(status) {
      if (status === 'approved') return 'success'
      if (status === 'rejected') return 'danger'
      if (status === 'pending') return 'warning'
      return 'info'
    },
    qualificationStatusLabel(status, fallback = '未认证') {
      if (status === 'approved') return '已通过'
      if (status === 'rejected') return '已驳回'
      if (status === 'pending') return '待审核'
      if (status === 'none') return '未认证'
      return status || fallback
    },
    async loadUsers() {
      this.loading = true
      try {
        this.users = await fetchUsers()
      } catch (error) {
        if (isAuthRedirectError(error)) return
        this.$message.error(error.message || '\u7528\u6237\u5217\u8868\u83b7\u53d6\u5931\u8d25')
      } finally {
        this.loading = false
      }
    },
    async viewUser(row) {
      try {
        this.currentUser = await fetchUserDetail(row.openid)
        this.drawerVisible = true
      } catch (error) {
        if (isAuthRedirectError(error)) return
        this.$message.error(error.message || '\u7528\u6237\u8be6\u60c5\u83b7\u53d6\u5931\u8d25')
      }
    },
    toggleBlacklist(row) {
      const action = row.blacklisted ? '\u53d6\u6d88\u62c9\u9ed1' : '\u62c9\u9ed1'
      this.$confirm(`\u786e\u8ba4${action}\u8be5\u7528\u6237\u5417\uff1f`, '\u63d0\u793a', { type: 'warning' })
        .then(async () => {
          await setUserBlacklist(row.openid, !row.blacklisted)
          this.$message.success('\u64cd\u4f5c\u6210\u529f')
          await this.loadUsers()
          if (this.currentUser && this.currentUser.openid === row.openid) {
            this.currentUser = await fetchUserDetail(row.openid)
          }
        })
        .catch(() => {})
    }
  }
}
</script>

<style scoped>
.user-cell {
  display: flex;
  align-items: center;
  gap: 10px;
}

.u-name {
  font-size: 14px;
  font-weight: 600;
}

.u-id {
  margin-top: 2px;
  color: #7d88a3;
  font-size: 12px;
}

.detail-wrap {
  padding: 0 8px;
}

.detail-avatar {
  width: 88px;
  height: 88px;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  background: #f8fafc;
}
</style>
